import { mergeClasses, nonNull, normalizeNumber } from 'util/commons';
import styles from './index.module.scss';
import { useTranslation } from 'react-i18next';

import { useEffect, useState } from 'react';
import { CustomCheckBox } from 'components/atoms/CustomCheckbox';
import { BottomField } from 'components/molecules/BottomField';
import CustomPagination from 'components/atoms/CustomPagination';
import {
  confirmReceives,
  getReceiveQRInfo,
  getReceives,
} from 'apis/compression_acceptance';
import { Receive } from 'types/compression';
import { getMaterialNamesBySite } from 'apis/commons';
import { Form } from 'react-final-form';
import { SelectField, SelectOption } from 'components/molecules/SelectField';
import { BookingQRScannedData } from 'util/Types';
import { toast } from 'react-toastify';
import { QRScanningProps } from '../QRScanning';
import QRScanningModal from '../QRScanningModal';
import { BookingQRType } from 'util/Enums';
import { createAxios } from 'ts/createAxios';
import { API_ENDPOINTS } from 'util/endPoints';
import { ExpandableList } from '../ExpandableList';
import { LARGER_PAGE_SIZE } from 'util/ConstantValues';

type ReceiveItem = {
  id: string;
  deliveryId?: number;
  materialName: string;
  processingObjectId?: string;
  processingObjectIds: string[];
  requestedDatetime: string;
  type: string;
  weight: string;
};

const CompressionAcceptancesScreen = () => {
  const { t } = useTranslation();

  const [filterData, setFilterData] = useState<{
    materialName: string | number;
    currentPage: number;
  }>({
    materialName: '',
    currentPage: 1,
  });

  const [materialOptions, setMaterialOptions] = useState<SelectOption[]>([]);

  const [selectedItems, setSelectedItems] = useState<Receive[]>([]);

  const [items, setItems] = useState<Receive[]>([]);

  const [totalPage, setTotalPage] = useState<number>(0);

  const [isOpenQRScanningModal, setIsOpenQRScanningModal] = useState(false);

  const fetchReceives = async () => {
    await getReceives({
      query: {
        materialName: filterData.materialName,
        page: filterData.currentPage,
      },
      successCallback: ({ data, total }) => {
        setItems(data);
        setTotalPage(Math.ceil(total / LARGER_PAGE_SIZE));
      },
      failedCallback: (error) => {
        console.error(error);
      },
    });
  };

  const handleSelectItem = (item: Receive) => {
    if (selectedItems.includes(item)) {
      setSelectedItems(selectedItems.filter((i) => i !== item));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const handleSelectAllItems = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items);
    }
  };

  const handleSelectMaterial = (value: string | number) => {
    setFilterData((prev) => ({
      ...prev,
      materialName: value,
      currentPage: 1,
    }));
  };

  const fetchMaterialOptions = async () => {
    getMaterialNamesBySite({
      screen: 'acceptance',
      successCallback: (data) => {
        const options = data.map((item) => ({
          label: item,
          value: item,
        }));
        setMaterialOptions(options);
      },
    });
  };

  const handleSubmit = async () => {
    const selectedDuplicatedMaterialNames =
      selectedItems.length > 0 &&
      !selectedItems.every(
        (item, _, array) => item.description === array[0].description
      );

    if (selectedDuplicatedMaterialNames) {
      toast.error(t('messages.M_please_select_same_material_name'));

      return;
    }

    await confirmReceives({
      formData: {
        deliveryIds: selectedItems
          .map((item) => item.deliveryId?.toString())
          .filter(Boolean),
        resourceIds: selectedItems
          .filter((item) => item.deliveryId == null)
          .flatMap((item) => item.targetIds),
      },
      successCallback: () => {
        toast.success(t('messages.M_135'));
        setSelectedItems([]);
        setFilterData((prev) => ({
          ...prev,
          currentPage: 1,
        }));
        fetchMaterialOptions();
      },
    });
  };

  const getIdentifier = (d: BookingQRScannedData & { qrCode: string }) => {
    if (d.type === BookingQRType.Emission) {
      return (d.deliveryId || d.processingObjectId)?.toString() || '';
    }

    if (d.type === BookingQRType.General) {
      return d.qrCode;
    }

    return d.processingObjectId?.toString() || '';
  };

  const handleScan: QRScanningProps<
    BookingQRScannedData & { qrCode: string },
    ReceiveItem
  >['onScan'] = async (qrCode, addItem) => {
    await getReceiveQRInfo({
      qrCode,
      successCallback: (data) => {
        if (data) {
          toast.success(t('qr_scan.success'));
          addItem({ ...data, qrCode });
        }
      },
    });
  };

  const handleQRSubmit = async ({
    checkedItemIds,
    groupedItems,
    onSuccess,
  }: {
    checkedItemIds: string[];
    groupedItems: {
      id: string;
      name: string;
      data: ReceiveItem[];
    }[];
    isCompleted: boolean;
    onSuccess: () => void;
  }) => {
    try {
      if (!checkedItemIds.length) return;

      const checkedItems = groupedItems.flatMap((group) =>
        group.data.filter((item) => checkedItemIds.includes(item.id))
      );

      const selectedDifferentMaterialNames = checkedItems.some(
        (item, _, array) => item.materialName !== array[0].materialName
      );

      if (selectedDifferentMaterialNames) {
        toast.error(t('messages.M_please_select_same_material_name'));

        return;
      }

      if (checkedItems.length > 100) {
        toast.error(t('messages.M_please_receive_no_more_than_100_items'));

        return;
      }

      const registeringEmissionDeliverIds: number[] = [];
      const registeringEmissionIds: string[] = [];
      const registeringResourceIds: string[] = [];
      const registeringDeliveryIds: number[] = [];

      checkedItems.forEach(({ deliveryId, processingObjectId, type }) => {
        if (type === BookingQRType.Emission) {
          if (deliveryId) {
            registeringEmissionDeliverIds.push(deliveryId);
          } else {
            registeringEmissionIds.push(nonNull(processingObjectId));
          }
        } else if (type === BookingQRType.General) {
          registeringDeliveryIds.push(nonNull(deliveryId));
        } else {
          registeringResourceIds.push(nonNull(processingObjectId));
        }
      });

      const promises: Promise<void>[] = [];

      if (
        registeringEmissionDeliverIds.length ||
        registeringEmissionIds.length
      ) {
        promises.push(
          createAxios().post(API_ENDPOINTS.COMPRESSION_RECEIVE_EMISSIONS, {
            deliveryIds: registeringEmissionDeliverIds,
            emissionIds: registeringEmissionIds,
          })
        );
      }

      if (registeringResourceIds.length || registeringDeliveryIds.length) {
        promises.push(
          createAxios().post(API_ENDPOINTS.COMPRESSION_RECEIVES, {
            deliveryIds: registeringDeliveryIds,
            resourceIds: registeringResourceIds,
          })
        );
      }

      await Promise.all(promises);

      toast.success(t('messages.M_135'));

      const totalItems = groupedItems.reduce(
        (sum, group) => sum + group.data.length,
        0
      );
      const remainingItems = totalItems - checkedItemIds.length;

      onSuccess();
      setFilterData((prev) => ({
        ...prev,
        currentPage: 1,
      }));
      fetchMaterialOptions();

      if (remainingItems === 0) {
        setIsOpenQRScanningModal(false);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message
          ? e.response.data.message
          : t('receives.signup_messages.failed')
      );
    }
  };

  useEffect(() => {
    setItems([]);
    setSelectedItems([]);
    fetchReceives();
  }, [filterData]);

  useEffect(() => {
    fetchMaterialOptions();
  }, []);

  return (
    <div className={styles.compressionAcceptance}>
      <div className={styles.header}>
        <Form onSubmit={() => {}}>
          {(props) => {
            return (
              <form onSubmit={props.handleSubmit}>
                <div>
                  <SelectField
                    name="materialName"
                    placeholder={t('common.material_name')}
                    options={[
                      {
                        label: t('purchase_page.receive.select_material_name'),
                        value: '',
                      },
                      ...materialOptions,
                    ]}
                    onChange={(value) =>
                      handleSelectMaterial(value as string | number)
                    }
                    isDarkmode
                    showPlaceholderOption
                  />
                </div>
              </form>
            );
          }}
        </Form>

        <button
          className={styles.buttonApply}
          onClick={() => setIsOpenQRScanningModal(true)}
        >
          {t('compression_acceptance.qr_scanning')}
        </button>
      </div>
      <div className={styles.tableWrapper}>
        <table
          className={mergeClasses(styles.tableAcceptances, 'primary-table')}
        >
          <thead>
            <tr>
              <th
                onClick={handleSelectAllItems}
                className={mergeClasses(styles.selection, styles.checkAll, {
                  [styles.disabled]: items.length === 0,
                })}
              >
                {selectedItems.length > 0 &&
                selectedItems.length === items.length
                  ? t('common.deselect_all')
                  : t('common.select_all')}
              </th>
              <th className={styles.resourceId}>
                {t('compression_acceptance.resource_id')}
              </th>
              <th className={styles.materialName}>
                {t('compression_acceptance.material_name')}
              </th>
              <th className={styles.from}>
                {t('compression_acceptance.from')}
              </th>

              <th className={styles.weight}>
                {t('compression_acceptance.weight')}
              </th>
              <th className={styles.receivingMethod}>
                {t('compression_acceptance.receiving_method')}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item, index) => (
                <tr key={index}>
                  <td className={styles.selection}>
                    <CustomCheckBox
                      id={index.toString()}
                      checked={selectedItems.includes(item)}
                      onChange={() => {
                        handleSelectItem(item);
                      }}
                      darkMode
                    />
                  </td>
                  <td className={styles.resourceId}>
                    <ExpandableList
                      data={item.targetIds}
                      title={t('common.resource_id') ?? ''}
                    />
                  </td>
                  <td className={styles.materialName}>{item.description}</td>
                  <td className={styles.from}>{item.deliveryFromSiteName}</td>
                  <td className={styles.weight}>{`${normalizeNumber({
                    value: item.weight,
                  })}kg`}</td>
                  <td className={styles.receivingMethod}>
                    {item.receivingMethod
                      ? t(
                          `compression_acceptance.${item.receivingMethod.toLowerCase()}`
                        )
                      : ''}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className={styles.msgError}>
                  {t('messages.M_008')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <BottomField>
        <div className={styles.bottom}>
          <div className={styles.bottomBtn}>
            <button
              className={styles.btnRequest}
              type="button"
              disabled={selectedItems.length === 0}
              onClick={handleSubmit}
            >
              {t('compression_acceptance.confirm_receipt')}
            </button>
          </div>

          <CustomPagination
            pageCount={totalPage}
            onPageChange={({ selected }) => {
              setFilterData((prev) => ({
                ...prev,
                currentPage: selected + 1,
              }));
            }}
            forcePage={filterData.currentPage - 1}
          />
        </div>
      </BottomField>

      {isOpenQRScanningModal && (
        <QRScanningModal<BookingQRScannedData & { qrCode: string }, ReceiveItem>
          onScan={handleScan}
          getItemId={(data) => getIdentifier(data)}
          getGroupId={(data) => data.materialName}
          getGroupName={(data) => data.materialName}
          toGroupItem={(data) => ({
            id: getIdentifier(data),
            weight: data.weight,
            deliveryId: data.deliveryId,
            materialName: data.materialName,
            processingObjectId: data.processingObjectId,
            processingObjectIds: data.processingObjectIds,
            requestedDatetime: data.requestedDatetime,
            type: data.type,
          })}
          getLatestScanDisplay={(data) => ({
            label: t('qr_scan.material_name') as string,
            name: data.materialName,
            value: data.weight,
            unit: 'kg',
          })}
          renderGroupItem={({ item }) => {
            return (
              <>
                {item.processingObjectIds.map((id) => (
                  <div className={styles.scanItem} key={id}>
                    <span>{id}</span>
                  </div>
                ))}
              </>
            );
          }}
          getGroupSummaryDisplay={({ group, checkedItemIds }) => {
            const total = group.data.reduce((sum, item) => {
              if (!checkedItemIds.includes(item.id)) return sum;
              return sum + (parseFloat(item.weight) || 0);
            }, 0);
            return {
              value: normalizeNumber({ value: total.toString() }),
              unit: 'kg',
            };
          }}
          getConfirmData={({ checkedItemIds, groupedItems }) => {
            let totalWeight = 0;
            const items: Record<string, { name: string; weight: string }> = {};

            groupedItems.forEach((group) => {
              const checked = group.data.filter((item) =>
                checkedItemIds.includes(item.id)
              );
              if (checked.length > 0) {
                const w = checked.reduce(
                  (sum, item) => sum + (parseFloat(item.weight) || 0),
                  0
                );
                totalWeight += w;
                items[group.id] = { name: group.name, weight: w.toString() };
              }
            });

            return { quantity: checkedItemIds.length, totalWeight, items };
          }}
          checkMode="group"
          onSubmit={handleQRSubmit}
          title={t('qr_scanning_modal.title') as string}
          finishText={t('qr_scanning_modal.finish_scanning') as string}
          onClose={() => setIsOpenQRScanningModal(false)}
        />
      )}
    </div>
  );
};

export default CompressionAcceptancesScreen;
